/**
 * parseFeedback(raw) — normalize whatever comes back from the evaluate endpoint.
 *
 * The backend now stores feedback as a JSON string:
 *   { summary, strengths[], weaknesses[], improvements[] }
 *
 * Older sessions may have a plain string or no feedback at all.
 * This helper always returns { summary, strengths, weaknesses, improvements }
 * so callers don't have to handle multiple shapes.
 */
export function parseFeedback(raw) {
  const empty = { summary: '', strengths: [], weaknesses: [], improvements: [] };

  if (raw == null || raw === '') return empty;

  // If it's already an object (somehow leaked through), stringify and reparse.
  if (typeof raw !== 'string') {
    try { return parseFeedback(JSON.stringify(raw)); } catch { return { ...empty, summary: String(raw) }; }
  }

  const trimmed = raw.trim();

  // Not JSON — it's a plain-text legacy feedback string.
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return { ...empty, summary: trimmed };
  }

  try {
    const parsed = JSON.parse(trimmed);

    // Structured shape: { summary, strengths[], weaknesses[], improvements[] }
    if (parsed && (parsed.summary != null || parsed.strengths || parsed.weaknesses || parsed.improvements)) {
      return {
        summary:      parsed.summary      ?? '',
        strengths:    Array.isArray(parsed.strengths)    ? parsed.strengths    : [],
        weaknesses:   Array.isArray(parsed.weaknesses)   ? parsed.weaknesses   : [],
        improvements: Array.isArray(parsed.improvements) ? parsed.improvements : [],
      };
    }

    // Legacy: { score, feedback: "string" } or { feedback: { ... } }
    if (parsed?.feedback) {
      return parseFeedback(
        typeof parsed.feedback === 'string'
          ? parsed.feedback
          : JSON.stringify(parsed.feedback)
      );
    }

    // Gemini raw response leaked through
    const geminiText = parsed?.candidates?.[0]?.content?.parts?.[0]?.text;
    if (geminiText) return parseFeedback(geminiText);

    // Unknown JSON — render as summary
    return { ...empty, summary: JSON.stringify(parsed, null, 2) };
  } catch {
    return { ...empty, summary: trimmed };
  }
}

/**
 * feedbackToPlainText(parsed) — collapse structured feedback into a single
 * readable string. Used for display contexts that only show a paragraph.
 */
export function feedbackToPlainText(parsed) {
  if (typeof parsed === 'string') return parsed;
  const parts = [];
  if (parsed?.summary)      parts.push(parsed.summary);
  if (parsed?.strengths?.length)    parts.push('Strengths: ' + parsed.strengths.join(', ') + '.');
  if (parsed?.weaknesses?.length)   parts.push('Areas to improve: ' + parsed.weaknesses.join(', ') + '.');
  if (parsed?.improvements?.length) parts.push('Tips: ' + parsed.improvements.join(' '));
  return parts.join('\n\n') || 'No feedback available.';
}
